import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { eDays } from 'src/app/modules/route-planner/constants/days.constants';
import { PICK_DAY, TIME, TYPE_TIME_24_HOURS } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { Day } from 'src/app/modules/route-planner/interfaces/route-planning/day.interface';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import {
  StopFullData,
  StopMessagesForDayResponse,
  StopTimesWithRoute,
} from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { SpecialMessage } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-search-station-deatiled-result-routes-calendar',
    templateUrl: './search-station-deatiled-result-routes-calendar.component.html',
    styleUrls: [
        './search-station-deatiled-result-routes-calendar.component.scss',
    ],
    standalone: false
})
export class SearchStationDeatiledResultRoutesCalendarComponent
  extends ResizeService
  implements OnInit, OnDestroy
{
  @Input() stop: StopFullData;
  @Output() closeCardEvent = new EventEmitter<void>();

  pickDayText: string
  timeText: string
  typeTime24HoursText: string
  private daySubject = new BehaviorSubject<Day | null>(null);
  day$ = this.daySubject.asObservable();
  isToday: boolean
  showLoader: boolean = false;

  timeControl = new FormControl<string>(this.utilsService.getCurrentTime());

  stopTimes: StopTimesWithRoute[];

  routeSpecialMessages: StopMessagesForDayResponse['routeSpecialMessages'] = {};
  stopSpecialMessages: SpecialMessage[] = [];

  routesInStop: Route[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private readonly utilsService: UtilsService,
    private readonly queryParamsService: QueryParamsService,
    private readonly scheduleService: ScheduleService,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.day$.subscribe((day) => {
      this.refreshRealTimeStopTimes(true);
      this.getStopMessagesForTheDay();
    });
    this.timeControl.valueChanges.subscribe((value) =>
      this.filterStopTimes(this.daySubject.getValue(), value)
    );

    // Start the timer after 1 minute (60000ms) and then run every minute
    interval(60000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshRealTimeStopTimes();
      });

      this.initializeMultiLanguageTexts()
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMultiLanguageTexts() {

    this.languageService.selectedLanguage$.subscribe((language) => {
      this.pickDayText = PICK_DAY[language.language]
      this.timeText = TIME[language.language]
      this.typeTime24HoursText = TYPE_TIME_24_HOURS[language.language]
    })
  }

  getDayDate(day: Day | null): Date {
    // If day is null, return today's date
    if (!day) {
      return new Date();
    }
    
    const today = new Date();
    const todayDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const requestedDayOfWeek = day.value; // eDays enum: 0 = Sunday, 1 = Monday, etc.
    
    // Calculate how many days ahead the requested day is
    let daysAhead = requestedDayOfWeek - todayDayOfWeek;
    
    // If the requested day is the same as today, return today
    if (daysAhead === 0) {
      return today;
    }
    
    // If the requested day is in the past (negative), add 7 days to get next week's date
    if (daysAhead < 0) {
      daysAhead += 7;
    }
    
    // Create a new date with the calculated days ahead
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysAhead);
    
    return targetDate;
  }

  getStopMessagesForTheDay(): void {
    const day = this.daySubject.getValue();
    const dayDate = this.getDayDate(day);
    this.scheduleService
      .getStopMessagesForTheDay(this.stop.stopcode, dayDate)
      .subscribe((response) => {
        if (response.success) {
          this.routeSpecialMessages = response.data.routeSpecialMessages ?? {};
          this.stopSpecialMessages = response.data.stopSpecialMessages ?? [];
        }
      });
  }

  refreshRealTimeStopTimes(showLoader: boolean = false) {
    if(showLoader) {
      this.showLoader = true;
    }
    const day = this.daySubject.getValue();
    const dayDate = this.getDayDate(day);
    this.scheduleService.getStopTimesAtStop(this.stop.stopcode, dayDate).subscribe((response) => {
      if (response.success) {
        this.stop.stopTimes = response.data.stopTimes;
        this.routesInStop = response.data.routesInStop;
        this.filterStopTimes(day, this.timeControl.value);
        this.showLoader = false;
      }
    });
  }

  filterStopTimes(day: Day | null, time: string | null) {
    // const dayDate = this.getDayDate(day);
    const dayToFilter: Day = day || this.daySubject.getValue();
    const timeToFilter: string = time || this.timeControl.value;
    this.isToday = dayToFilter.value === new Date().getDay();

    if (dayToFilter && timeToFilter) {
      this.stopTimes = this.stop.stopTimes.filter((stopTime) => {
       
        const parseTime = (time: string): Date => {
          const [hours, minutes, seconds = 0] = time.split(':').map(Number);
          return new Date(0, 0, 0, hours, minutes, seconds);
        };
        const timeValue = parseTime(timeToFilter);
        const stopTimeToUse = this.isToday && stopTime.realTimeDepartureTime ? stopTime.realTimeDepartureTime : stopTime.departureTime
        const timeStopTime = parseTime(stopTimeToUse);
        stopTime.departureTimeToShow = stopTimeToUse
        return (
          stopTime.calendar[dayToFilter.name.toLocaleLowerCase()] === 1 &&
          timeStopTime.getTime() - timeValue.getTime() >= 0
        );
      })
      .sort((a, b) => {
        const parseTime = (time: string): Date => {
          const [hours, minutes, seconds = 0] = time.split(':').map(Number);
          return new Date(0, 0, 0, hours, minutes, seconds);
        };
        const timeA = parseTime(a.departureTimeToShow);
        const timeB = parseTime(b.departureTimeToShow);;
        return timeA.getTime() - timeB.getTime();
      })
      ;
    }
  }

  pickDay(day: Day) {
    this.daySubject.next(day);
  }

  pickLine(line: Route) {
    const trustedBase = environment.maslulan_url.replace(/\/+$/, '');
    const url = `${trustedBase}/?tab=lines&saveParams=true&routeId=${line.routeId}&line=${line.routeName}&oppositeRouteId=${line.oppositeDirectionId}&boardingStation=${this.stop.stopcode}&dropingStation=${line.stops[line.stops.length - 1].stopcode}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getSelectedDate(): Date | null {
    const day = this.daySubject.getValue();
    return day ? this.getDayDate(day) : null;
  }
}
